import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authorizationService, CreateRoleData, UpdateRoleData } from '../services';

interface RoleFilters {
  page?: number;
  per_page?: number;
  search?: string;
}

// Roles
export const useRoles = (filters?: RoleFilters) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['roles', filters],
    queryFn: async () => {
      const response = await authorizationService.getRoles(filters?.page);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateRoleData) => authorizationService.createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateRoleData }) =>
      authorizationService.updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => authorizationService.deleteRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });

  const assignPermissionsMutation = useMutation({
    mutationFn: ({ roleId, permissionIds }: { roleId: number; permissionIds: number[] }) =>
      authorizationService.assignPermissions(roleId, permissionIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });

  const syncPermissionsMutation = useMutation({
    mutationFn: ({ roleId, permissionIds }: { roleId: number; permissionIds: number[] }) =>
      authorizationService.syncPermissions(roleId, permissionIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });

  const removePermissionMutation = useMutation({
    mutationFn: ({ roleId, permissionId }: { roleId: number; permissionId: number }) =>
      authorizationService.removePermission(roleId, permissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });

  return {
    roles: data,
    isLoading,
    error,
    createRole: createMutation.mutateAsync,
    updateRole: updateMutation.mutateAsync,
    deleteRole: deleteMutation.mutateAsync,
    assignPermissions: assignPermissionsMutation.mutateAsync,
    syncPermissions: syncPermissionsMutation.mutateAsync,
    removePermission: removePermissionMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
};

export const useRole = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['roles', id],
    queryFn: async () => {
      const response = await authorizationService.getRole(id);
      return response.data;
    },
    enabled: !!id,
  });

  return {
    role: data,
    isLoading,
    error,
  };
};

export const useRolePermissions = (roleId: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['roles', roleId, 'permissions'],
    queryFn: async () => {
      const response = await authorizationService.getRolePermissions(roleId);
      return response.data;
    },
    enabled: !!roleId,
  });

  return {
    permissions: data,
    isLoading,
    error,
  };
};

// Permissions
export const usePermissions = (page?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['permissions', page],
    queryFn: async () => {
      const response = await authorizationService.getPermissions(page);
      return response.data;
    },
  });

  return {
    permissions: data,
    isLoading,
    error,
  };
};

export const useAllPermissions = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['permissions', 'all'],
    queryFn: async () => {
      const response: any = await authorizationService.getAllPermissions();
      const result = response.data;
      if (Array.isArray(result)) return result;
      if (result?.data && Array.isArray(result.data)) return result.data;
      return [];
    },
  });

  return {
    permissions: (data || []) as any[],
    isLoading,
    error,
  };
};

export const usePermissionsByModule = (module: string) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['permissions', 'module', module],
    queryFn: async () => {
      const response = await authorizationService.getPermissionsByModule(module);
      return response.data;
    },
    enabled: !!module,
  });

  return {
    permissions: data,
    isLoading,
    error,
  };
};
